import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { type ReactNode, useState } from "react";

import { Payer } from "graphql_globals";
import { Select } from "common/core/form/select";
import { Paragraph } from "common/core/typography";
import Button from "common/core/button";
import { useMutation } from "util/graphql";
import AlertMessage from "common/core/alert_message";

import UpdateOrgDefaultPayerMutation from "../graphql/update_organization_default_payer_mutation.graphql";
import Styles from "./index.module.scss";

const MESSAGES = defineMessages({
  whoPaysLabel: {
    id: "384ec8a8-c51d-4ecb-bae6-cb0924b80cce",
    defaultMessage: "Who pays",
  },
  title: {
    id: "5e7184ce-06cb-4479-81d3-9c355ee30085",
    defaultMessage: "Payment",
  },
});

export function PayerSelectRow({
  payer,
  setPayer,
  companyName,
  organizationId,
  onClose,
  onChangesSaved,
  submitButtonLabel,
}: {
  payer: Payer;
  companyName: string | null;
  organizationId: string;
  setPayer: (selectedPayer: Payer) => void;
  onClose?: () => void;
  onChangesSaved: () => void;
  submitButtonLabel?: ReactNode;
}) {
  const intl = useIntl();
  const [mutationError, setMutationError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const updateOrgDefaultPayerMutation = useMutation(UpdateOrgDefaultPayerMutation);
  const payerItems = [
    { label: companyName || "Company", value: Payer.ORGANIZATION },
    { label: "Signer", value: Payer.CUSTOMER },
  ];

  const saveSignerAsPayer = async () => {
    setMutationError(false);
    setIsLoading(true);
    await updateOrgDefaultPayerMutation({
      variables: {
        input: {
          id: organizationId,
          defaultPayer: payer,
        },
      },
    })
      .then(() => onChangesSaved())
      .catch(() => setMutationError(true))
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <h3 className={Styles.profileSectionHeader}>{intl.formatMessage(MESSAGES.title)}</h3>

      <div className={Styles.row}>
        <div className={Styles.rowTitle}>
          <span>{intl.formatMessage(MESSAGES.whoPaysLabel)}</span>
        </div>
        <div className={Styles.selectContainer}>
          <Select
            id="who-pays"
            data-automation-id="select-payment-field"
            aria-invalid={false}
            items={payerItems}
            onChange={(event) => setPayer(event.target.value as Payer)}
            selectedValue={payer}
          />
          {payer === Payer.CUSTOMER && (
            <div className={Styles.signerSelectedDetails}>
              <Paragraph
                size="defaultSize"
                textColor="subtle"
                className="section-header-disclaimer"
              >
                <FormattedMessage
                  id="fa88fd38-17ed-4d4b-8e8d-0457cb7f55a9"
                  defaultMessage="Signers will be prompted for payment information when they complete each transaction."
                />
              </Paragraph>
            </div>
          )}
        </div>
      </div>
      {payer === Payer.CUSTOMER && (
        <div className={Styles.buttonBar}>
          {onClose && (
            <Button
              buttonColor="dark"
              variant="tertiary"
              key="cancel"
              onClick={onClose}
              isLoading={isLoading}
            >
              <FormattedMessage id="5c4d3f96-c6c8-47cf-8ff6-7fa2fd99d69d" defaultMessage="Cancel" />
            </Button>
          )}
          <Button
            buttonColor="action"
            variant="primary"
            key="Save changes"
            onClick={saveSignerAsPayer}
            data-automation-id="save-changes-button"
            isLoading={isLoading}
          >
            {submitButtonLabel ? (
              submitButtonLabel
            ) : (
              <FormattedMessage
                id="44213035-1629-4bbe-9561-cccac6663aca"
                defaultMessage="Save payment"
              />
            )}
          </Button>
        </div>
      )}

      {mutationError && (
        <AlertMessage kind="danger">
          <FormattedMessage
            id="92e7b9ef-4e42-438a-9814-f95e6155aa9d"
            defaultMessage="Sorry, something went wrong. Please try again."
          />
        </AlertMessage>
      )}
    </>
  );
}
